import React, { useEffect, useId, useState } from "react";
import { Popover } from "@commonsku/design-system";
import withReducers from "../../store/withReducers";
import myTasksSlice, { MY_TASKS, MyTasksListKey } from "../../redux/myTasks";
import { useMyTasks } from "../../hooks";
import TasksMenuTrigger from "./tasks-menu/TasksMenuTrigger";
import TasksMenuTabs from "./tasks-menu/TasksMenuTabs";

const TasksMenu = () => {
  const triggerId = useId();
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<MyTasksListKey>(MY_TASKS);
  const {
    myTasks,
    assignedTasks,
    myTasksNewCount,
    assignedTasksNewCount,
    fetchList,
    fetchAssignedTasksCount,
    markSeen,
    toggleComplete,
  } = useMyTasks();

  const activeList = activeTab === MY_TASKS ? myTasks : assignedTasks;

  useEffect(() => {
    fetchList(MY_TASKS);
    fetchAssignedTasksCount();
  }, [fetchList, fetchAssignedTasksCount]);

  const handleOpenChange = (details: { open: boolean }) => {
    setOpen(details.open);
    if (details.open) {
      // Refresh the active tab (picks up anything that changed while
      // closed). Don't mark it seen yet - only once the user switches
      // away from it or closes the popover.
      fetchList(activeTab);
    } else {
      // Popover closed while viewing this tab - mark it seen now.
      markSeen(activeTab);
    }
  };

  const handleTabChange = (tab: MyTasksListKey) => {
    // Mark the tab being left as seen, not the one being switched to -
    // newly posted tasks should stay "new" until the user moves on from
    // viewing them.
    markSeen(activeTab);
    setActiveTab(tab);
    // Popover is already open here - fetch the tab being switched to.
    fetchList(tab);
  };

  const handleToggleComplete = (noteId: string, complete: boolean) => {
    toggleComplete(activeTab, noteId, complete);
  };

  return (
    <Popover.Root
      ids={{ trigger: triggerId }}
      open={open}
      onOpenChange={handleOpenChange}
      positioning={{ placement: "bottom-end", strategy: "fixed" }}
    >
      <TasksMenuTrigger
        triggerId={triggerId}
        myTasksNewCount={myTasksNewCount}
        assignedTasksNewCount={assignedTasksNewCount}
      />
      <Popover.Positioner>
        <Popover.Content width="md">
          <Popover.Body padding="1">
            <TasksMenuTabs
              activeTab={activeTab}
              onTabChange={handleTabChange}
              myTasksNewCount={myTasksNewCount}
              assignedTasksNewCount={assignedTasksNewCount}
              panelProps={{
                notes: activeList.notes,
                total: activeList.total,
                seenAt: activeList.seenAt,
                loading: activeList.loading,
                onToggleComplete: handleToggleComplete,
              }}
            />
          </Popover.Body>
        </Popover.Content>
      </Popover.Positioner>
    </Popover.Root>
  );
};

export default withReducers(TasksMenu, {
  [myTasksSlice.name]: myTasksSlice.reducer,
});
